import styled from 'styled-components';

import { BaseInputFieldProps } from 'components/Input/InputField/BaseInputField/types';

const getPadding = (
  hasStartIcon: boolean,
  hasEndIcon: boolean,
  hasLabel: boolean,
  hasClear: boolean,
  theme: any,
) => {
  let top = theme.spacing.xl;
  let bottom = theme.spacing.xl;
  let left = theme.spacing.xl;
  let right = theme.spacing.xl;

  if (hasLabel) {
    //shift input text down to make space for label
    top = theme.spacing['2Xl'];
    bottom = theme.spacing.md;
  }

  if (hasStartIcon) {
    left = theme.spacing['4Xl'];
  }

  if ((hasEndIcon || hasClear) && !(hasEndIcon && hasClear)) {
    right = theme.spacing['4Xl'];
  } else {
    right = theme.spacing['7Xl'];
  }

  return `${top} ${right} ${bottom} ${left}`;
};

export const Container = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.inter}, Arial, Helvetica, sans-serif !important; // otherwise overridden by Brandon Font on deployed environments
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const MultilineContainer = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.inter}, Arial, Helvetica, sans-serif !important; // otherwise overridden by Brandon Font on deployed environments
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
`;

export const Input = styled.input<BaseInputFieldProps>`
  box-sizing: border-box;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.background.default};
  padding: ${({ startIcon, endIcon, theme, label, value }) =>
    getPadding(!!startIcon, !!endIcon, !!value && !!label, false, theme)};

  color: ${({ theme }) => theme.text.strong};
  ${({ theme }) => theme.body.regular.md}

  width: 100%;
  height: ${({ theme }) => theme.spacing['6Xl']};
  font-family: ${({ theme }) => theme.fontFamily.inter}, Arial, Helvetica, sans-serif !important; // otherwise overridden by Brandon Font on deployed environments

  ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.default)};

  &:focus-visible,
  &:focus {
    outline: none;
    ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.strong)};

    padding: ${({ startIcon, endIcon, theme, label, value }) =>
      getPadding(!!startIcon, !!endIcon, !!value && !!label, true, theme)};
  }

  &:disabled {
    background: ${({ theme }) => theme.background.disabledSubtle};
    ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.disabled)};
    color: ${({ theme }) => theme.text.disabled};
  }

  ::placeholder {
    text-transform: capitalize;
    ${({ theme }) => theme.body.regular.md}
    color:   ${({ theme }) => theme.text.weak};
  }

  &:invalid {
    ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.critical)};
  }
`;

export const TextArea = styled.textarea<BaseInputFieldProps>`
  display: block;

  box-sizing: border-box;
  border: none;
  resize: none;

  ${({ theme }) => theme.body.regular.md}
  background: ${({ theme }) => theme.background.default};

  width: 100%;
  height: ${({ theme }) => theme.spacing['8Xl']};
  padding: ${({ startIcon, endIcon, theme, label, value }) =>
    getPadding(!!startIcon, !!endIcon, !!value && !!label, false, theme)};

  ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.default)};

  color: ${({ theme }) => theme.text.strong};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  overflow: hidden;

  &:disabled {
    background: ${({ theme }) => theme.background.disabledSubtle};
    ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.disabled)};
    color: ${({ theme }) => theme.text.disabled};
  }

  ::placeholder {
    text-transform: capitalize;
    ${({ theme }) => theme.body.regular};
    color: ${({ theme }) => theme.text.weak};
  }

  &:invalid {
    ${({ theme }) => theme.innerAlignBorder(theme.border.xs, theme.border.critical)};
  }
`;

export const Label = styled.label`
  position: absolute;
  padding: 0px ${({ theme }) => theme.spacing.xl};
  ${({ theme }) => theme.label.xs};
  color: ${({ theme }) => theme.text.weak};

  top: ${({ theme }) => theme.spacing.md};
  left: 0;
`;

export const ErrorLabel = styled.div`
  ${({ theme }) => theme.body.regular.xs};
  color: ${({ theme }) => theme.text.critical};
`;

export const BaseIcon = styled.div<{ multiline?: boolean }>`
  position: absolute;
  height: ${({ theme }) => theme.spacing.xl};
  top: ${({ theme, multiline }) => (multiline ? theme.spacing.xl : '20px')};
`;

export const StartIcon = styled(BaseIcon)`
  left: ${({ theme }) => theme.spacing.xl};
`;

export const EndIcon = styled(BaseIcon)`
  right: ${({ theme }) => theme.spacing.xl};
`;

export const ClearInputIcon = styled(BaseIcon)<Partial<BaseInputFieldProps>>`
  cursor: pointer;
  right: ${({ theme, endIcon }) => (endIcon ? theme.spacing['4Xl'] : theme.spacing.xl)};
`;

export const InformationArea = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;
